import React from "react";

import { useDispatch, useSelector } from "react-redux";

import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { Button, SecondaryButton } from "components/Form/Button";
import { Column, Row } from "components/Layout";
import { BoldText, Text, Subtitle } from "components/Text";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  ZoomyDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { ApiModel } from "constants/loading";

import { ModalTypes } from "constants/navigation";

import { getBuyerBalanceSummaryByBuyerId } from "selectors/clearingSaleInvoice";
import { BuyerPaymentSummaryData } from "constants/payments";
import { getContextByModalType } from "selectors";

import { BusinessPaymentsTable } from "components/BusinessPaymentsModal/BusinessPaymentsTable";
import { openDispatchingModal } from "actions";
import { PurchaseSummaryTable } from "components/EditPaymentModal/SummaryTable";
import { DeploymentBusinessName } from "components/ClearingSaleCards/BusinessComponents";
import { formatDollarsFromCents } from "lib";

interface BuyerPaymentSummaryModalProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dialog event and target types
  onClose: (event?: any, target?: any) => void;
}

type BuyerPaymentSummaryModalContext = {
  buyerId: string;
} | null;

function useModalContext(): BuyerPaymentSummaryModalContext {
  return useSelector(getContextByModalType(ModalTypes.BuyerPaymentSummary));
}

export function BuyerPaymentSummaryModal({
  onClose,
}: BuyerPaymentSummaryModalProps) {
  const dispatch = useDispatch();

  const modalContext: BuyerPaymentSummaryModalContext = useModalContext();

  const { buyerId } = modalContext || {};

  const balanceSummary = useSelector<State, BuyerPaymentSummaryData>(
    getBuyerBalanceSummaryByBuyerId(buyerId),
  );

  if (buyerId == null) {
    return null;
  }

  function onClickAddPayment() {
    dispatch(
      openDispatchingModal(
        ModalTypes.EditPayment,
        window.location.hash,
        {
          values: {
            totalAmountCents: balanceSummary?.totals?.dueCents ?? null,
          },
        },
        { fromBusinessId: buyerId },
      ),
    );
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md" fullWidth>
      <WaitForSync
        requiredData={[
          ApiModel.SALE_LOTS,
          ApiModel.PAYMENTS,
          ApiModel.BUSINESSES,
          ApiModel.DEPLOYMENTS,
          ApiModel.BILLING_RUNS,
          ApiModel.BILLING_DOCUMENTS,
        ]}
      >
        <DialogTitle onClose={onClose}>Payments</DialogTitle>
        <DialogContent dividers>
          <Row alignBaseline marginVertical={1} gridGap={1}>
            <BoldText fontSize="charlie">
              <DeploymentBusinessName businessId={buyerId} />
            </BoldText>
            <Row gridGap={1}>
              <Text fontSize="charlie">Due</Text>
              <BoldText fontSize="charlie">
                {formatDollarsFromCents(balanceSummary.totals.dueCents, {
                  format: true,
                })}
              </BoldText>
            </Row>
          </Row>
          <PurchaseSummaryTable businessId={buyerId} />

          <Column relative>
            <Row
              justifyBetween
              alignCenter
              marginBottom={3}
              minHeight="40px"
              gridGap={1}
            >
              <Subtitle>Payments</Subtitle>
              <Row flexShrink={0} paddingVertical={1}>
                <Button onClick={onClickAddPayment} whiteSpaceNoWrap>
                  Add Payment
                </Button>
              </Row>
            </Row>

            <Column relative>
              <ModalTableWrapper minHeight={150}>
                <WaitForSync requiredData={[ApiModel.PAYMENTS]}>
                  <BusinessPaymentsTable businessId={buyerId} />
                </WaitForSync>
              </ModalTableWrapper>
            </Column>
          </Column>
        </DialogContent>
        <DialogActions>
          <Row justifyCenter fullWidth>
            <SecondaryButton data-tour="close" type="button" onClick={onClose}>
              Close
            </SecondaryButton>
          </Row>
        </DialogActions>
      </WaitForSync>
    </ZoomyDialog>
  );
}
